/**
 * LoadingState Component
 *
 * A wrapper component that shows skeleton placeholders during loading
 * and reveals content once data is available.
 *
 * Usage:
 * <LoadingState
 *   isLoading={!data}
 *   skeleton={<SkeletonCard />}
 * >
 *   <ActualContent data={data} />
 * </LoadingState>
 */

import React from 'react'

interface LoadingStateProps {
  /** Whether data is still loading */
  isLoading: boolean
  /** Skeleton placeholder to show while loading */
  skeleton: React.ReactNode
  /** Content to show when loaded */
  children: React.ReactNode
  /** Optional minimum height to prevent layout shift */
  minHeight?: number | string
  /** Optional className for the container */
  className?: string
  /** Whether to show a revalidating indicator (subtle opacity) */
  isRevalidating?: boolean
}

export function LoadingState({
  isLoading,
  skeleton,
  children,
  minHeight,
  className = '',
  isRevalidating = false,
}: LoadingStateProps) {
  if (isLoading) {
    return (
      <div
        className={`loading-state ${className}`}
        style={{ minHeight }}
        role="status"
        aria-busy="true"
        aria-label="Loading content"
      >
        {skeleton}
      </div>
    )
  }

  return (
    <div
      className={`loaded-state ${isRevalidating ? 'revalidating' : ''} ${className}`}
      style={{ minHeight }}
    >
      {children}
    </div>
  )
}

/**
 * LoadingList Component
 *
 * Renders a list of skeleton items while loading.
 */
interface LoadingListProps {
  isLoading: boolean
  skeleton: React.ReactNode
  count?: number
  children: React.ReactNode
  className?: string
  isRevalidating?: boolean
}

export function LoadingList({
  isLoading,
  skeleton,
  count = 3,
  children,
  className = '',
  isRevalidating = false,
}: LoadingListProps) {
  if (isLoading) {
    return (
      <div
        className={`loading-list ${className}`}
        role="status"
        aria-busy="true"
        aria-label="Loading list"
      >
        {Array.from({ length: count }).map((_, i) => (
          <React.Fragment key={i}>{skeleton}</React.Fragment>
        ))}
      </div>
    )
  }

  return (
    <div className={`loaded-list ${isRevalidating ? 'revalidating' : ''} ${className}`}>
      {children}
    </div>
  )
}

/**
 * RevalidatingIndicator Component
 *
 * Shows a subtle progress bar when data is being revalidated in the background.
 */
interface RevalidatingIndicatorProps {
  isRevalidating: boolean
}

export function RevalidatingIndicator({ isRevalidating }: RevalidatingIndicatorProps) {
  if (!isRevalidating) return null

  return (
    <div className="revalidating-indicator" role="status" aria-label="Refreshing data">
      <div className="revalidating-bar" />
    </div>
  )
}

export default LoadingState
